'use client'

import { useAtomValue, useSetAtom } from 'jotai'
import { usePathname } from 'next/navigation'
import { useCallback } from 'react'

import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import {
  COMPONENT_REGISTRY,
  type ComponentKey,
  getComponentConfigByLabel,
} from '@/features/dashboard/lib/component-registry'
import { findEmptyPosition, GRID_COLS } from '@/features/dashboard/lib/grid-layout-config'
import { compactLayout } from '@/features/dashboard/lib/grid-math'
import { isSharePreviewPath } from '@/features/dashboard/lib/share-layout'
import {
  resolveWidgetConstraints,
  WIDGET_DEFAULT_SIZES,
} from '@/features/dashboard/lib/widget-sizes'
import { track } from '@/lib/analytics'
import store from '@/lib/configs/store-config'
import { useAddWidget } from '@/service/ui-state.hook'
import {
  activePageIdAtom,
  dashboardLayoutsAtom,
  generateNewInstanceId,
  getDefaultLayoutForPath,
  recentlyAddedWidgetAtom,
} from '@/state/dashboard-layouts'

export function useAddDashboardWidget() {
  const pathname = usePathname()
  const cleanPathname = pathname.split('?')[0]
  const activePageId = useAtomValue(activePageIdAtom)
  // A shared-layout preview is read-only, and activePageId still points at the
  // viewer's own page — both the local layout key and the AddWidget below would
  // otherwise target it, so adding a widget here would silently land on a page
  // that is not even on screen.
  const isPreview = isSharePreviewPath(cleanPathname)
  const layoutKey = activePageId && !isPreview ? `/trade/${activePageId}` : cleanPathname
  const setAllLayouts = useSetAtom(dashboardLayoutsAtom)
  const { isLoggedIn, requireAuth } = useAuthGuard()

  const { mutateAsync: doAddWidget } = useAddWidget({
    onError: (err) => console.error('[useAddDashboardWidget] addWidget failed', err),
  })

  const addWidget = useCallback(
    (componentKey: ComponentKey) => {
      if (isPreview) return
      requireAuth(() => {
        const config = COMPONENT_REGISTRY[componentKey]
        if (!config) {
          console.error(`Component config not found for key: ${componentKey}`)
          return
        }

        // Read + compute + write synchronously instead of inside a setState
        // updater: React may re-run an updater, and the persistence call and the
        // analytics event below must each fire exactly once per user action.
        const prev = store.get(dashboardLayoutsAtom)
        const currentLayout = prev[layoutKey] || getDefaultLayoutForPath(layoutKey)
        const instanceId = generateNewInstanceId(
          config.key,
          currentLayout.activeComponents.map((c) => c.id)
        )

        const updatedLayouts = { ...currentLayout.layouts }
        // Compact first: currentLayout.layouts.studio can hold stale gaps left
        // behind by a filter-only removal (removeComponent never re-floats
        // survivors), so finding empty space against the raw array can hand
        // the new widget a slot that visually collides with an item that
        // hasn't re-settled yet — which then reads as the two widgets
        // swapping once canonicalLayouts compacts on the next render.
        const studioLayout = compactLayout(updatedLayouts.studio || [])
        const { w, h } = WIDGET_DEFAULT_SIZES[config.key]
        const { minW, minH, maxW, maxH } = resolveWidgetConstraints(
          config.key,
          GRID_COLS.studio
        ) ?? { minW: w, minH: h }
        const position = findEmptyPosition(studioLayout, w, h, GRID_COLS.studio, minW)
        const effectiveW = position.w

        const newItem = {
          i: instanceId,
          x: position.x,
          y: position.y,
          w: effectiveW,
          h,
          minW,
          minH,
          ...(maxW && { maxW }),
          ...(maxH && { maxH }),
        }

        updatedLayouts.studio = [...studioLayout, newItem]

        setAllLayouts({
          ...prev,
          [layoutKey]: {
            activeComponents: [
              ...currentLayout.activeComponents,
              { id: instanceId, componentKey: config.key },
            ],
            layouts: updatedLayouts,
            instanceCounters: currentLayout.instanceCounters,
          },
        })
        store.set(recentlyAddedWidgetAtom, { id: instanceId, at: Date.now() })

        if (isLoggedIn && activePageId) {
          // Duplicate / template / import go through useAddWidget too but never
          // through here, so this is the one place a user-added widget is tracked.
          void doAddWidget({
            pageId: activePageId,
            widgetId: instanceId,
            widgetType: config.key,
            x: newItem.x,
            y: newItem.y,
            w: effectiveW,
            h,
            minW: newItem.minW ?? 2,
            minH: newItem.minH ?? 2,
            config: {},
          })
            .then(() => track('Add Widget to Page', { widget_name: config.key }))
            .catch(() => {}) // onError above already logs
        }
      })
    },
    [activePageId, doAddWidget, isLoggedIn, isPreview, layoutKey, requireAuth, setAllLayouts]
  )

  const addWidgetByLabel = useCallback(
    (label: string) => {
      const config = getComponentConfigByLabel(label)
      if (!config) {
        console.error(`Component config not found for label: ${label}`)
        return
      }
      addWidget(config.key)
    },
    [addWidget]
  )

  return { addWidget, addWidgetByLabel }
}
